
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { filter, finalize, firstValueFrom, from, Observable, Subscription, switchMap, take, tap } from 'rxjs';

import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { MessageService } from 'primeng/api';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';

import { BrandIdentity } from '../../models/brand-identity';
import { BrandIdentityService } from '../../services/brand-identity.service';
import { ImageHelper } from '../../../PageBuilder/helpers/image.helper';

@Component({
    selector: 'agpr-brand-identity-card',
    templateUrl: './brand-identity-card.component.html',
    styleUrls: ['./brand-identity-card.component.scss'],
    standalone: false
})
export class BrandIdentityCardComponent implements OnInit, OnDestroy {
    readonly buttonTypes = ButtonTypesEnum;

    private subscriptions: Subscription[] = [];
    private _cachedBrand: BrandIdentity;

    @Input() id: string = 'brandIdentityCard';
    @Input() showBorder: boolean = true;
    @Input() set brand(value: BrandIdentity) {
        if (value?.logo?.source !== this._brand?.logo?.source) {
            this.logoLoadingError = false;
        }
        this._brand = value.clone();
        this._cachedBrand = this._brand.clone();
    };

    _brand: BrandIdentity;
    brandList: BrandIdentity[] = [];
    aiGenerating: boolean = false;
    logoLoadingError: boolean = false;

    constructor(
        private messageService: MessageService,
        private localizationService: LocalizationService,
        private brandIdentityService: BrandIdentityService
    ) {}

    ngOnInit(): void {
        this.subscriptions.push(
            this.brandIdentityService.all$.pipe(
                filter(x => !!x)
            ).subscribe(brands => {
                this.brandList = [...brands];
            }),
        );
    }

    private async validateLogoSource(brand: BrandIdentity): Promise<BrandIdentity> {
        if (brand.logo?.source) {
            try {
                await ImageHelper.getImageDimensions(brand.logo.source);
            } catch {
                brand.logo.source = '';
            }
        }
        return brand;
    }

    onBrandNameChanged() {
        this._brand.name = this._brand.name.trim();
        this._brand.setLogoPlaceholder();
    }

    isNameDuplicated(): Observable<boolean> {
        const brandName = this._brand.name;
        return this.brandIdentityService.isNameDuplicated$(brandName).pipe(
            tap(isDuplicated => {
                if (isDuplicated) {
                    this.messageService.add({
                        severity : messageSeverityEnum.error,
                        summary: 'Common.brandIdentity.duplicateName.heading',
                        detail: this.localizationService.transform('Common.brandIdentity.duplicateName.message', {
                            name: brandName
                        })
                    });
                }
            }),
            take(1)
        );
    }

    async create() {
        const isNameDuplicated = await firstValueFrom(
            this.isNameDuplicated()
        );
        if (isNameDuplicated) {
            return;
        }

        const brand = this._brand.clone();
        const linkedBrand = brand.linkedBrand;
        if (linkedBrand) {
            const linkedCompetitor = linkedBrand.competitors.find(x => x.name === this._cachedBrand.name);
            linkedCompetitor.name = brand.name;
        }

        this.brandIdentityService.create(brand).subscribe(newBrand => {
            this.brandIdentityService.setCurrentResource(newBrand);
        });
    }

    async aiCreate() {
        const isNameDuplicated = await firstValueFrom(
            this.isNameDuplicated()
        );
        if (isNameDuplicated) {
            return;
        }
        this.aiGenerating = true;
        this.brandIdentityService.aiCreate(this._brand.name).pipe(
            switchMap(brand => from(this.validateLogoSource(brand))),
            switchMap(brand => {
                brand.name = this._brand.name;
                if (this._brand.linkedBrand) {
                    const linkedBrand = this._brand.linkedBrand.clone();
                    const linkedCompetitor = linkedBrand.competitors.find(x => x.name === this._cachedBrand.name);
                    linkedCompetitor.name = brand.name;
                    brand.linkedBrand = linkedBrand;
                }
                return this.brandIdentityService.create(brand);
            }),
            finalize(() => {
                this.aiGenerating = false;
            })
        ).subscribe(brand => {
            this.brandIdentityService.setCurrentResource(brand);
        });
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
